const sectionHeading = { color: 'var(--text-primary)' }
const subHeading = { color: 'var(--text-primary)' }
const accent = { color: 'var(--color-accent)' }
const muted = { color: 'var(--text-muted)' }
const border = { borderBottom: '1px solid var(--border-color)' }

/**
 * Shown on the privacy notice at sign-up, matching the two standalone privacy
 * pages. Informational only: a privacy notice INFORMS, it is not consented to,
 * so changing this date deliberately creates no version string and triggers no
 * re-acceptance. The TERMS are the consented artefact — `terms_acceptances`
 * stores one row per accepted version — and version-aware re-acceptance for
 * those is still a pre-launch item (STATE.md).
 */
const PRIVACY_LAST_UPDATED = '27 October 2026'

export function TermsContent() {
  return (
    <div className="space-y-8 text-sm leading-relaxed">
      <section>
        <h2 className="text-base font-bold mb-3" style={sectionHeading}>Introduction</h2>
        <p>
          These Terms of Service ("Terms") govern your use of Lentals (a trading name of Ogen Group Ltd,
          Company No. 17042093), accessible at{' '}
          <a href="https://lentals.com" className="font-medium hover:underline" style={accent}>lentals.com</a>.
          By creating an account, you agree to these Terms.
        </p>
      </section>

      <section>
        <h2 className="text-base font-bold mb-3" style={sectionHeading}>Eligibility</h2>
        <p>
          Lentals is for UK-based landlords, letting agents, and property managers aged 18 or over with legal
          authority to manage the properties added to the platform.
        </p>
      </section>

      <section>
        <h2 className="text-base font-bold mb-3" style={sectionHeading}>Subscription and billing</h2>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Billed monthly or annually via Stripe</li>
          <li>14-day free trial; card required at sign-up</li>
          <li>Tiers: Starter, Standard, Professional (see lentals.com/pricing)</li>
          <li>Cancellations take effect at end of current billing period — no partial refunds</li>
          <li>Pricing changes notified 30 days in advance</li>
        </ul>
      </section>

      <section>
        <h2 className="text-base font-bold mb-3" style={sectionHeading}>Your responsibilities</h2>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Ensure accuracy of all property, tenant, and compliance data entered</li>
          <li>Serve legally required documents to tenants (Lentals provides the workflow; compliance responsibility remains with the landlord)</li>
          <li>Maintain valid Right to Rent checks independently</li>
          <li>Not use the platform for any unlawful purpose</li>
          <li>Keep your account credentials secure</li>
        </ul>
      </section>

      <section>
        <h2 className="text-base font-bold mb-3" style={sectionHeading}>Our responsibilities</h2>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Provide the platform with reasonable availability (we target 99.5% uptime)</li>
          <li>Maintain the audit trail accurately and securely</li>
          <li>Store data in accordance with our Privacy Policy</li>
          <li>Notify you of any material changes to the service or these Terms</li>
        </ul>
      </section>

      <section>
        <h2 className="text-base font-bold mb-3" style={sectionHeading}>Limitation of liability</h2>
        <p>
          Lentals (Ogen Group Ltd, Company No. 17042093) provides tools to assist with compliance. We do not
          provide legal advice. The landlord remains solely responsible for compliance with the Renters' Rights
          Act 2025, UK GDPR, and all other applicable legislation.
        </p>
        <p className="mt-2">
          Our total liability to you shall not exceed the fees paid by you in the 12 months preceding the claim.
        </p>
      </section>

      <section>
        <h2 className="text-base font-bold mb-3" style={sectionHeading}>Termination</h2>
        <p>
          Either party may terminate at any time. On termination, your data is retained per our Privacy Policy
          retention schedule and then deleted or anonymised accordingly.
        </p>
      </section>

      <section>
        <h2 className="text-base font-bold mb-3" style={sectionHeading}>Governing law</h2>
        <p>
          These Terms are governed by the laws of England and Wales. Disputes shall be subject to the exclusive
          jurisdiction of the courts of England and Wales.
        </p>
      </section>
    </div>
  )
}

export function PrivacyContent() {
  return (
    <div className="space-y-8 text-sm leading-relaxed">
      <p className="text-xs mb-4" style={subHeading}>
        Last updated: {PRIVACY_LAST_UPDATED}
      </p>

      <section>
        <h2 className="text-base font-bold mb-3" style={sectionHeading}>Who we are</h2>
        <p>
          Lentals (a trading name of Ogen Group Ltd, Company No. 17042093) is a UK property management SaaS
          platform. Ogen Group Ltd is the data controller registered with the Information Commissioner's Office
          (ICO). ICO registration number: ZC124026.
        </p>
        <p className="mt-2">
          For any privacy-related queries, contact us at{' '}
          <a href="mailto:support@lentals.com" className="font-medium hover:underline" style={accent}>support@lentals.com</a>.
        </p>
      </section>

      <section>
        <h2 className="text-base font-bold mb-3" style={sectionHeading}>What data we collect and why</h2>

        <h3 className="font-semibold mt-4 mb-2" style={subHeading}>Landlords</h3>
        <p className="text-xs mb-1.5" style={muted}>Lawful basis: contract performance + legitimate interest</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Name, email address, phone number</li>
          <li>Billing address and payment method (via Stripe — we never store card data)</li>
          <li>Property addresses and details</li>
          <li>Tenancy records and financial data</li>
        </ul>

        <h3 className="font-semibold mt-4 mb-2" style={subHeading}>Tenants</h3>
        <p className="text-xs mb-1.5" style={muted}>Lawful basis: legitimate interest for service messages; consent for marketing</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Name, phone number, email address</li>
          <li>Tenancy details (property address, rent, deposit scheme)</li>
          <li>Maintenance issue reports, photos, and messages</li>
          <li>Document access and acknowledgement records</li>
          <li>Right to Rent documents (special category — extra protections apply)</li>
        </ul>

        <h3 className="font-semibold mt-4 mb-2" style={subHeading}>Contractors</h3>
        <p className="text-xs mb-1.5" style={muted}>Lawful basis: legitimate interest</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Name, phone number</li>
          <li>Job records and communication history</li>
        </ul>
      </section>

      <section>
        <h2 className="text-base font-bold mb-3" style={sectionHeading}>How we use your data</h2>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>To operate the platform and deliver the service</li>
          <li>To serve required tenancy documents and log acknowledgements (legal compliance under Renters' Rights Act 2025)</li>
          <li>To send service notifications (issue updates, status changes) — legitimate interest</li>
          <li>To send marketing messages — only with explicit opt-in consent</li>
          <li>To maintain legally-required audit trails</li>
        </ul>
      </section>

      <section>
        <h2 className="text-base font-bold mb-3" style={sectionHeading}>Third-party processors</h2>
        <p className="mb-3">All third-party processors listed below have Data Processing Agreements (DPAs) in place.</p>
        <div className="overflow-x-auto rounded-lg" style={{ border: '1px solid var(--border-color)' }}>
          <table className="w-full text-xs">
            <thead>
              <tr style={{ backgroundColor: 'var(--bg-surface)' }}>
                <th className="text-left px-3 py-2 font-semibold" style={{ ...subHeading, ...border }}>Service</th>
                <th className="text-left px-3 py-2 font-semibold" style={{ ...subHeading, ...border }}>Purpose</th>
                <th className="text-left px-3 py-2 font-semibold" style={{ ...subHeading, ...border }}>Data shared</th>
              </tr>
            </thead>
            <tbody>
              {[
                { service: 'Supabase', purpose: 'Hosting, sign-in, database and file storage', data: 'All information you and your tenants or contractors enter, and files you upload' },
                { service: 'Vercel', purpose: 'Serving the website and app', data: 'IP address, browser and request details' },
                { service: 'Stripe', purpose: 'Payment processing', data: 'Billing address, email' },
                { service: 'Resend', purpose: 'Sending email', data: 'Email addresses and the contents of those emails' },
                { service: 'Sentry', purpose: 'Error and performance monitoring', data: 'Error reports, the pages you visited, browser and device details' },
                { service: 'Google, Mozilla or Apple', purpose: 'Delivering push notifications to your browser or device', data: 'A notification address for your browser, and the notification contents, encrypted' },
                { service: 'Anthropic', purpose: 'Scanning receipts and certificates you upload', data: 'Document contents (supplier names, property addresses, engineer names, certificate numbers)' },
                { service: 'Xero', purpose: 'Accounting sync — only if you connect a Xero account', data: 'Rent and transaction records for your properties' },
              ].map((row) => (
                <tr key={row.service}>
                  <td className="px-3 py-2 font-medium" style={{ ...subHeading, ...border }}>{row.service}</td>
                  <td className="px-3 py-2" style={border}>{row.purpose}</td>
                  <td className="px-3 py-2" style={border}>{row.data}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="text-base font-bold mb-3" style={sectionHeading}>Where your data is stored and sent</h2>
        <p>
          Our hosting, email, payment, monitoring and document-scanning providers are based in or operate from the United States. That means some of your information is transferred outside the UK.
        </p>
        <p className="mt-3">
          Where that happens, we rely on the transfer safeguards in each provider's data processing terms — the UK International Data Transfer Addendum, or the EU Standard Contractual Clauses with the UK Addendum, depending on the provider. You can ask us which applies to a particular provider.
        </p>
        <p className="mt-3">
          Our database and file storage are hosted in the EU (Stockholm).
        </p>
      </section>

      <section>
        <h2 className="text-base font-bold mb-3" style={sectionHeading}>Data retention</h2>
        <div className="overflow-x-auto rounded-lg" style={{ border: '1px solid var(--border-color)' }}>
          <table className="w-full text-xs">
            <thead>
              <tr style={{ backgroundColor: 'var(--bg-surface)' }}>
                <th className="text-left px-3 py-2 font-semibold" style={{ ...subHeading, ...border }}>Data</th>
                <th className="text-left px-3 py-2 font-semibold" style={{ ...subHeading, ...border }}>Retention</th>
              </tr>
            </thead>
            <tbody>
              {[
                { data: 'Audit trail and tenancy documents', retention: '6 years after tenancy ends' },
                { data: 'Tenant personal details', retention: 'Anonymised 12 months after tenancy ends' },
                { data: 'Contractor personal details', retention: 'Anonymised 12 months after last job' },
                { data: 'Landlord account data', retention: 'Deleted 12 months after account closure' },
                { data: 'Financial records', retention: '6 years (HMRC requirement)' },
                { data: 'OTP codes', retention: 'Deleted immediately after use or expiry' },
              ].map((row) => (
                <tr key={row.data}>
                  <td className="px-3 py-2 font-medium" style={{ ...subHeading, ...border }}>{row.data}</td>
                  <td className="px-3 py-2" style={border}>{row.retention}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="text-base font-bold mb-3" style={sectionHeading}>Your rights under UK GDPR</h2>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong style={subHeading}>Right to access</strong> — Subject Access Request, responded to within 30 days</li>
          <li><strong style={subHeading}>Right to rectification</strong> — ask us to correct inaccurate data</li>
          <li><strong style={subHeading}>Right to erasure</strong> — where not overridden by legal retention obligations</li>
          <li><strong style={subHeading}>Right to data portability</strong> — receive your data in a structured, machine-readable format</li>
          <li><strong style={subHeading}>Right to object</strong> — object to processing based on legitimate interest</li>
          <li><strong style={subHeading}>Right to restrict processing</strong> — request that we limit how we use your data</li>
          <li><strong style={subHeading}>Right to withdraw consent</strong> — at any time, for any processing based on consent</li>
        </ul>
        <p className="mt-3">
          To exercise any of these rights, email{' '}
          <a href="mailto:support@lentals.com" className="font-medium hover:underline" style={accent}>support@lentals.com</a>.
        </p>
      </section>

      <section>
        <h2 className="text-base font-bold mb-3" style={sectionHeading}>Cookies</h2>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong style={subHeading}>Essential cookies:</strong> session management and authentication — no consent required</li>
          <li><strong style={subHeading}>Analytics cookies:</strong> only loaded after explicit consent</li>
          <li>No marketing or analytics cookies load before consent is given</li>
        </ul>
      </section>

      <section>
        <h2 className="text-base font-bold mb-3" style={sectionHeading}>Complaints</h2>
        <p>
          Contact us at{' '}
          <a href="mailto:support@lentals.com" className="font-medium hover:underline" style={accent}>support@lentals.com</a>.
          You also have the right to complain to the ICO:{' '}
          <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="font-medium hover:underline" style={accent}>ico.org.uk</a>
          {' '}/ 0303 123 1113.
        </p>
      </section>
    </div>
  )
}
